import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useRoutes } from 'react-router-dom';
import { IAppWrapperProps } from './app/types';
import FullScreenLoader from './components/common/loading-spinner/FullScreenLoader';
import VXErrorBoundary from './components/visionx/error-boundary/VXErrorBoundary';
import { EnvironmentContext } from './providers/environment/EnvironmentContext';
import { SessionContext } from './providers/session/SessionContext';
import { SessionStatus } from './providers/session/types';
import { SystemContext } from './providers/system/SystemContext';
import { unSubscribe } from './utils/Utils';
import { useTheme } from 'src/app/custom-hooks/useTheme';
import { useAuthGuard } from 'src/app/custom-hooks/useAuthGuard.ts';

const App = (props: IAppWrapperProps): React.ReactElement => {
  const sessionContext = useContext(SessionContext);
  const environmentContext = useContext(EnvironmentContext);
  const systemContext = useContext(SystemContext);

  useTheme();

  const [sessionStatus, setSessionStatus] = useState(SessionStatus.LOADING);
  const [systemLoaded, setSystemLoaded] = useState(false);
  const [homePage, setHomePage] = useState<string | undefined>();
  const hasSession = sessionStatus === SessionStatus.VALID;
  const validSession: boolean = hasSession;

  const memoizedRoutes = useMemo(() => {
    // Session validation in progress → show spinner, don't mount login routes
    if (!hasSession && sessionStatus === SessionStatus.LOADING) {
      return [
        {
          path: '*',
          element: <FullScreenLoader />,
        },
      ];
    }

    // Unauthenticated → pre-auth routes
    if (!hasSession) {
      return props.menuService.createPreAuthenticatedRoutes();
    }

    // Authenticated but system not loaded yet → fallback spinner route
    if (!systemLoaded) {
      return [
        {
          path: '*',
          element: <FullScreenLoader />,
        },
      ];
    }

    // Authenticated and system loaded → post-auth routes
    const menuGroups = systemContext.getSystemConfig()?.menuGroups || [];

    return props.menuService.createPostAuthenticatedRoutes(menuGroups);
  }, [systemLoaded, systemContext, props.menuService, hasSession, sessionStatus]);

  const routing = useRoutes(memoizedRoutes);
  const _origPath = window.location.pathname + window.location.search;

  useEffect(() => {
    if (!hasSession) {
      if (_origPath !== '/login') {
        sessionContext.setOriginalPath(_origPath);
      }
    }
  }, []);

  useEffect(() => {
    const envSub = environmentContext.watchEnvironment().subscribe((env) => {
      if (env.loaded) setHomePage(env.homePage);
    });
    const sysSub = systemContext.watchSystemLoaded().subscribe(setSystemLoaded);
    const sessSub = sessionContext.watchSessionStatus().subscribe(setSessionStatus);

    return () => {
      unSubscribe(envSub);
      unSubscribe(sysSub);
      unSubscribe(sessSub);
    };
  }, [environmentContext, systemContext, sessionContext]);

  useAuthGuard({
    validSession,
    hasSession,
    systemLoaded,
    sessionLoading: sessionStatus === SessionStatus.LOADING,
    homePage,
    menuService: props.menuService,
  });

  const isLoading =
    (!routing && hasSession) || // authenticated but system not loaded yet
    (!hasSession && sessionStatus === SessionStatus.LOADING); // unauthenticated session loading

  return (
    <>
      {isLoading ? (
        <FullScreenLoader />
      ) : (
        <VXErrorBoundary
          component={<React.Suspense fallback={<FullScreenLoader />}>{routing}</React.Suspense>}
        />
      )}
    </>
  );
};

export default App;
